// "Where are you going?" → up to 3 parking options → Start.
// Search is Photon (OSM, free); options reuse the existing nearby-parking API.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Loader2, ChevronLeft, MapPin, Zap } from "lucide-react";
import { searchDestinations, resolveDestination } from "../lib/geocode";
import { fetchNearbyParking } from "../lib/parkingPlaces";
import { buildOptions, formatWalk, resortOptions, filterWalkable, PLACE_MAX_M } from "../lib/journey";
import { attachWalkingRoutes } from "../lib/walking";

// Progressive: start tight around the destination and only widen when the
// closer stages cannot offer enough. One API call serves all three stages.
const STAGES_M = [500, 700, PLACE_MAX_M];
const ENOUGH_OPTIONS = 3;
import { track } from "../lib/analytics";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

const OPTION_RADIUS_M = 900;

export default function DestinationSheet({ open, onClose, bias, liveSpots, journey, onStart }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [dest, setDest] = React.useState(null);
  const [options, setOptions] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState(null);
  const searchedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults([]);
    searchedRef.current = false;
    const d = journey?.destination || null;
    setDest(d);
    setOptions(null);
    setSelectedId(journey?.selected?.id || null);
  }, [open, journey]);

  // Debounced as-you-type search, one in-flight request at a time.
  React.useEffect(() => {
    if (!open || dest || query.trim().length < 3) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await searchDestinations(query, { bias, signal: controller.signal }));
      } catch {
        /* keep previous results */
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, dest]);

  // Options for the chosen destination.
  React.useEffect(() => {
    if (!open || !dest) return;
    let alive = true;
    setOptions(null);
    (async () => {
      let places = [];
      try {
        places = await fetchNearbyParking({
          lat: dest.lat,
          lng: dest.lng,
          radius_m: OPTION_RADIUS_M,
          preference: "any",
          limit: 20,
        });
      } catch {
        places = [];
      }
      if (!alive) return;
      let ranked = [];
      for (const radius of STAGES_M) {
        ranked = buildOptions({ destination: dest, places, liveSpots, maxPlaceM: radius });
        if (ranked.length >= ENOUGH_OPTIONS) break;
      }
      setOptions(ranked);
      // Real pedestrian routes for the shortlist, then re-rank and drop anything
      // beyond the 15-minute walking limit. The second pass covers an option
      // promoted into the visible top 3 (routes are cached).
      const withWalk = await attachWalkingRoutes(dest, ranked, 5);
      if (!alive) return;
      const promoted = resortOptions(filterWalkable(withWalk));
      const final = resortOptions(filterWalkable(await attachWalkingRoutes(dest, promoted, 3)));
      if (!alive) return;
      setOptions(final);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dest]);

  const onQueryChange = (v) => {
    setQuery(v);
    if (!searchedRef.current && v.trim().length >= 3) {
      searchedRef.current = true;
      track("destination_search_started");
    }
  };

  const chooseDestination = async (d) => {
    // Google predictions only carry coordinates after a Place Details call.
    const resolved = d.needs_details ? await resolveDestination(d).catch(() => null) : d;
    if (!resolved) return;
    setDest(resolved);
    setSelectedId(null);
    track("destination_selected", { has_label: !!resolved.label });
  };

  const top3 = (options || []).slice(0, 3);
  const selected = top3.find((o) => o.id === selectedId) || null;

  const start = () => {
    if (!selected || !dest) return;
    const backups = (options || []).filter((o) => o.id !== selected.id);
    track("parking_journey_started", { kind: selected.kind, distance_m: selected.distance_m });
    onStart(dest, selected, backups);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2200] bg-black/65"
            {...dismissProps}
            data-testid="destination-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[2201] max-h-[82vh] overflow-y-auto bg-[#141416] border-t border-white/10 rounded-t-3xl p-4 pb-7 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:rounded-3xl sm:border sm:w-[440px] sm:max-w-[92vw]"
            data-testid="destination-sheet"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  Parking assistant
                </div>
                <div className="font-heading text-lg font-semibold text-white mt-0.5 truncate">
                  {dest ? dest.name : "Where are you going?"}
                </div>
                {dest?.label && (
                  <div className="text-[11px] text-zinc-500 truncate mt-0.5">{dest.label}</div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                data-testid="destination-close"
                aria-label="Close destination search"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {!dest ? (
              <div data-testid="destination-search-view">
                <div className="flex items-center gap-2 h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 focus-within:border-[#ccff00]/60 transition-colors">
                  <Search className="w-4 h-4 text-zinc-500 shrink-0" />
                  <input
                    type="text"
                    value={query}
                    autoFocus
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder="Where are you going?"
                    className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-zinc-600"
                    data-testid="destination-input"
                  />
                  {searching && <Loader2 className="w-4 h-4 animate-spin text-[#ccff00]" />}
                </div>

                <div className="mt-2.5 space-y-1.5" data-testid="destination-results">
                  {results.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => chooseDestination(r)}
                      className="w-full p-3 rounded-2xl bg-[#0a0a0c] border border-white/10 flex items-start gap-2.5 text-left active:scale-[0.99] transition-transform"
                      data-testid={`destination-result-${r.id}`}
                    >
                      <MapPin className="w-4 h-4 text-[#ccff00] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[13px] text-white truncate">{r.name}</div>
                        {r.label && (
                          <div className="text-[11px] text-zinc-500 truncate mt-0.5">{r.label}</div>
                        )}
                      </div>
                    </button>
                  ))}
                  {!results.length && query.trim().length >= 3 && !searching && (
                    <div className="py-6 text-center text-[13px] text-zinc-500" data-testid="destination-no-results">
                      Nothing found in Malta for that.
                    </div>
                  )}
                  {query.trim().length < 3 && (
                    <p className="pt-3 text-[12px] text-zinc-500 leading-snug">
                      Type a place, street or locality. You can also close this and pick parking
                      straight off the map.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div data-testid="destination-options-view">
                <button
                  type="button"
                  onClick={() => {
                    setDest(null);
                    setOptions(null);
                    setSelectedId(null);
                  }}
                  className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 mb-2"
                  data-testid="destination-back"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Change destination
                </button>

                {options === null ? (
                  <div className="py-8 flex items-center justify-center text-zinc-500 text-[13px]">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Finding parking near there…
                  </div>
                ) : top3.length === 0 ? (
                  <div className="py-8 text-center" data-testid="destination-options-empty">
                    <div className="text-[13px] text-zinc-300">No reliable parking options found nearby yet.</div>
                    <div className="text-[12px] text-zinc-500 mt-1">
                      Try a nearby landmark, or browse the map.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5" data-testid="destination-options">
                    {top3.map((o) => {
                      const on = o.id === selectedId;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => {
                            setSelectedId(o.id);
                            track("parking_option_selected", { kind: o.kind, distance_m: o.distance_m });
                          }}
                          className={`w-full p-3 rounded-2xl flex items-center gap-3 text-left transition-colors ${
                            on
                              ? "bg-[#ccff00]/10 border border-[#ccff00]/50"
                              : "bg-[#0a0a0c] border border-white/10"
                          }`}
                          data-testid={`journey-option-${o.id}`}
                        >
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                              o.source === "live"
                                ? "bg-[#ccff00]/15 border border-[#ccff00]/40"
                                : "bg-white/5 border border-white/10"
                            }`}
                          >
                            {o.source === "live" ? (
                              <Zap className="w-4 h-4 text-[#ccff00]" />
                            ) : (
                              <MapPin className="w-4 h-4 text-zinc-300" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-white truncate">
                              {o.type_label}
                            </div>
                            <div className="text-[12px] text-zinc-400 mt-0.5">
                              {formatWalk(o)}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{o.status}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {selected && (
                  <button
                    type="button"
                    onClick={start}
                    className="mt-3 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm active:scale-[0.98] transition-transform"
                    data-testid="journey-start-btn"
                  >
                    Start
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
